import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/admin/AuthContext";
import { formatApiErrorDetail } from "@/admin/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back, Schuyler.");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#141414] border border-[#374151] text-white placeholder:text-white/40 px-4 py-3 focus:outline-none focus:border-[#D86C1E] focus:ring-2 focus:ring-[#D86C1E]/40 transition-all";

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <Logo className="h-14 w-14 mb-4" inverted />
          <h1 className="font-heading text-2xl font-black uppercase tracking-tight text-white">
            Admin Login
          </h1>
          <p className="text-sm text-white/50 mt-1">Blessed Roofing · Lead Manager</p>
        </div>

        <form
          data-testid="admin-login-form"
          onSubmit={submit}
          className="bg-[#1A1A1A] border border-[#374151] p-8 space-y-4"
        >
          <input
            data-testid="login-email"
            type="email"
            className={inputCls}
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <input
            data-testid="login-password"
            type="password"
            className={inputCls}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            type="submit"
            data-testid="login-submit-btn"
            disabled={loading}
            className="group w-full inline-flex items-center justify-center gap-2 bg-[#D86C1E] hover:bg-[#B0551A] disabled:opacity-60 text-white font-heading font-bold text-base uppercase tracking-wide px-8 py-4 transition-all hover:-translate-y-0.5"
          >
            <Lock className="h-4 w-4" />
            {loading ? "Signing in…" : "Sign In"}
            {!loading && (
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            )}
          </button>
        </form>

        <a
          href="/"
          className="block text-center text-xs uppercase tracking-widest text-white/40 hover:text-[#D86C1E] mt-6 transition-colors"
        >
          ← Back to website
        </a>
      </div>
    </div>
  );
}
